import { differenceInDays, subDays } from 'date-fns';
import { FunctionComponent, memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnalyticChartInterval, useUserEngagementQuery } from '../../../../../services';
import { durationFormatter } from '../../../../../shared';
import { COLORS } from '../../Analytics';
import { ChartWrapper, Loader, StatsItem, TimeSeriesChart } from '../../helpers';
import { UserProps } from '../User';

import classes from '../../Analytics.module.scss';

const TYPE_LOCALISE_KEY = 'chartTypes.user-engagement';

export const UserEngagement: FunctionComponent<UserProps> = memo(({ from, to, platform }) => {
  const { t } = useTranslation();

  const { data: userEngagement, isFetching: isFetchingUserEngagement } = useUserEngagementQuery({
    from,
    to,
    interval: AnalyticChartInterval.AUTO,
    platform,
  });

  const { data: userEngagementStats, isFetching: isFetchingUserEngagementStats } =
    useUserEngagementQuery({
      from,
      to,
      platform,
    });

  const daysDiff = differenceInDays(new Date(to), new Date(from)) + 1;

  const { data: userEngagementStatsPrev, isFetching: isFetchingUserEngagementStatsPrev } =
    useUserEngagementQuery({
      from: subDays(new Date(from), daysDiff).toISOString(),
      to: subDays(new Date(to), daysDiff).toISOString(),
      platform,
    });

  const [userEngagementLabels, setUserEngagementLabels] = useState<string[]>([]);

  useEffect(() => {
    if (userEngagement && !userEngagementLabels.length) {
      setUserEngagementLabels(userEngagement.labels);
    }
  }, [userEngagement, userEngagementLabels.length]);

  const renderStats = useMemo(() => {
    if (isFetchingUserEngagementStats || isFetchingUserEngagementStatsPrev) {
      return <Loader stats />;
    }

    if (!userEngagementStats || !userEngagementStatsPrev) {
      return null;
    }

    const { datasets } = userEngagementStats;
    const { datasets: datasetsPrev } = userEngagementStatsPrev;

    return (
      <div className={classes['analytics__chart-stats']}>
        {datasets.map(({ label, data }, index) => {
          const isDisabled = !userEngagementLabels.includes(label);
          const isClickable = userEngagementLabels.length > 1 || isDisabled;

          return (
            <StatsItem
              key={label}
              label={label}
              localiseKey={`userEngagementLabels.${label}`}
              color={COLORS[index]}
              value={data[0] ?? 0}
              valuePrev={datasetsPrev.find((set) => set.label === label)?.data[0] ?? 0}
              valueFormatter={durationFormatter}
              labels={userEngagementLabels}
              setLabels={setUserEngagementLabels}
              isDisabled={isDisabled}
              isClickable={isClickable}
              columnDisplay
            />
          );
        })}
      </div>
    );
  }, [
    isFetchingUserEngagementStats,
    isFetchingUserEngagementStatsPrev,
    userEngagementLabels,
    userEngagementStats,
    userEngagementStatsPrev,
  ]);

  return (
    <ChartWrapper typeLocaliseKey={TYPE_LOCALISE_KEY} infoLocaliseKey={'userEngagement.info'}>
      {renderStats}
      {userEngagement && (
        <TimeSeriesChart
          timeSeries={userEngagement}
          labels={userEngagementLabels}
          localiseKeyLabels={'userEngagementLabels'}
          loading={isFetchingUserEngagement}
          ariaLabel={t(TYPE_LOCALISE_KEY)}
          valueFormatter={durationFormatter}
          margin={{ top: 20, right: 40, bottom: 50, left: 80 }}
        />
      )}
    </ChartWrapper>
  );
});
